import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createPortal } from 'react-dom';
import {
  UserTodoItem,
  GetUserTodosResponse,
  AgentEvents,
  UserTodoUpdatedEvent,
} from '@dreamai/types';
import { useSearchParams } from 'react-router-dom';
import { useAgent } from '../../contexts/AgentContext';
import { usePusher } from '../../contexts/PusherContext';
import { useMobileNav } from '../../contexts/MobileNavContext';
import api from '../../api';
import TasksSkeleton from './sidebar/TasksSkeleton';
import AddTodoModal from './sidebar/AddTodoModal';
import MobileNewTaskSheet from './reminders/MobileNewTaskSheet';
import ProjectListsBar, { PERSONAL_LIST_ID } from './reminders/ProjectListsBar';
import ListMembersPanel from './reminders/ListMembersPanel';
import AssigneePicker from './reminders/AssigneePicker';
import MemberAvatar from './reminders/MemberAvatar';
import { useTodoLists } from './reminders/useTodoLists';
import {
  apiErrorMessage,
  describeTodoProvenance,
} from './reminders/todoProvenance';
import { formatDueDate, isDueDateOverdue } from '../../utils/dueDate';
import { ReactComponent as PlusAddIcon } from '../../icons/plus-add.svg';
import { ReactComponent as CalendarOutlineIcon } from '../../icons/calendar-outline.svg';
import { ReactComponent as VerticalDotsIcon } from '../../icons/vertical-dots.svg';
import { ReactComponent as CheckSquareIcon } from '../../icons/check-square.svg';
import { ReactComponent as TodoistLogo } from '../../icons/todoist-logo.svg';
import { ReactComponent as AsanaLogo } from '../../icons/asana-logo.svg';
import './reminders-page.scss';

// ─── Helpers ──────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#3B82F6',
};

// ─── Component ────────────────────────────────────────────────

interface RemindersPageProps {
  /** Hide the internal "Reminders" heading (e.g. when rendered inside the app-view layout). */
  hideHeading?: boolean;
  /** Report a summary subtitle (e.g. "12 In-progress • 485 Completed") to the host. */
  onSubtitleChange?: (subtitle: string) => void;
}

const RemindersPage: React.FC<RemindersPageProps> = ({
  hideHeading,
  onSubtitleChange,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { activeAgent } = useAgent();
  const { isMobile } = useMobileNav();
  const [searchParams, setSearchParams] = useSearchParams();
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  const userFirstName =
    (user?.given_name as string | undefined) ||
    (user?.nickname as string | undefined) ||
    (typeof user?.name === 'string' ? user.name.split(' ')[0] : undefined) ||
    'You';

  const currentUserId = user?.sub ?? '';

  const [userTodos, setUserTodos] = useState<UserTodoItem[]>([]);
  const [addTodoModalOpen, setAddTodoModalOpen] = useState(false);
  const [selectedListId, setSelectedListId] =
    useState<string>(PERSONAL_LIST_ID);
  const [membersPanelOpen, setMembersPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [todoistConnected, setTodoistConnected] = useState(false);
  const [asanaConnected, setAsanaConnected] = useState(false);

  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [taskNotice, setTaskNotice] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Animation state
  const [completingItems, setCompletingItems] = useState<Set<string>>(
    new Set(),
  );

  const todoSound = useMemo(() => new Audio('/todo-sound.mp3'), []);
  const playTodoSound = useCallback(() => {
    todoSound.currentTime = 0;
    todoSound.play().catch(() => {});
  }, [todoSound]);

  // ─── Data Fetching ────────────────────────────────────────────

  const fetchTodos = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const agentFilter = activeAgent?.id ? `&agentId=${activeAgent.id}` : '';
      const response = await api.get<GetUserTodosResponse>(
        `/user-todos?assigneeType=user${agentFilter}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUserTodos(response.data.todos || []);
    } catch (err) {
      console.error('Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, activeAgent?.id]);

  const fetchTodoistStatus = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/todoist-link', { headers: { Authorization: `Bearer ${token}` } });
      setTodoistConnected(res.data.linkedAccounts.length > 0);
    } catch (err) {
      console.error('Error fetching Todoist status:', err);
    }
  }, [getAccessTokenSilently]);

  const fetchAsanaStatus = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/asana-link', { headers: { Authorization: `Bearer ${token}` } });
      setAsanaConnected(res.data.linkedAccounts.length > 0);
    } catch (err) {
      console.error('Error fetching Asana status:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchTodos();
    fetchTodoistStatus();
    fetchAsanaStatus();
  }, [fetchTodos, fetchTodoistStatus, fetchAsanaStatus]);

  // ─── Shared todo lists ────────────────────────────────────────

  const handleListsChanged = useCallback(() => {
    fetchTodos();
  }, [fetchTodos]);

  const {
    lists,
    invitations,
    loading: listsLoading,
    createList,
    renameList,
    archiveList,
    removeMember,
    respondToInvite,
  } = useTodoLists(userChannel, handleListsChanged);

  const selectedList = useMemo(
    () => lists.find((l) => l.id === selectedListId) ?? null,
    [lists, selectedListId],
  );

  // Deep link (`?tab=reminders&list=<id>`, e.g. from a WhatsApp message):
  // open that list once lists have loaded, then drop the param so later
  // tab changes are not overridden. Pending invitations stay on the Inbox
  // where the invitation card is shown.
  const urlListId = searchParams.get('list');
  useEffect(() => {
    if (!urlListId || listsLoading) return;
    if (lists.some((l) => l.id === urlListId)) {
      setSelectedListId(urlListId);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('list');
        return next;
      },
      { replace: true },
    );
  }, [urlListId, listsLoading, lists, setSearchParams]);

  // Fall back to the Inbox if the selected list disappears (archived, or
  // we were removed from it).
  useEffect(() => {
    if (urlListId) return;
    if (selectedListId !== PERSONAL_LIST_ID && !selectedList) {
      setSelectedListId(PERSONAL_LIST_ID);
      setMembersPanelOpen(false);
    }
  }, [urlListId, selectedListId, selectedList]);

  const handleCreateList = useCallback(
    async (name: string) => {
      const created = await createList(name);
      if (created) setSelectedListId(created.id);
      return created !== null;
    },
    [createList],
  );

  const handleRespondToInvite = useCallback(
    async (listId: string, response: 'accept' | 'decline') => {
      const result = await respondToInvite(listId, response);
      if (result?.list) {
        setSelectedListId(result.list.id);
        fetchTodos();
      }
    },
    [respondToInvite, fetchTodos],
  );

  // ─── Real-time Pusher updates ─────────────────────────────────

  useEffect(() => {
    if (!userChannel) return;

    const handleUserTodoUpdated = (data: UserTodoUpdatedEvent) => {
      if (!data.todoId) return;
      if (data.deleted) {
        setUserTodos((prev) => prev.filter((t) => t.id !== data.todoId));
        return;
      }
      setUserTodos((prev) => {
        const exists = prev.some((t) => t.id === data.todoId);
        if (!exists) {
          fetchTodos();
          return prev;
        }
        return prev.map((t) => {
          if (t.id !== data.todoId) return t;
          const updates: Partial<UserTodoItem> = {};
          if ((data as any).priority !== undefined)
            updates.priority = (data as any).priority;
          if ((data as any).dueDate !== undefined)
            updates.dueDate = (data as any).dueDate;
          if ((data as any).description !== undefined)
            updates.description = (data as any).description;
          if (data.status !== undefined)
            updates.status = data.status as UserTodoItem['status'];
          return { ...t, ...updates };
        });
      });
    };

    userChannel.bind(AgentEvents.USER_TODO_UPDATED, handleUserTodoUpdated);
    return () => {
      userChannel.unbind(AgentEvents.USER_TODO_UPDATED, handleUserTodoUpdated);
    };
  }, [userChannel, fetchTodos]);

  // ─── Dropdown Menu ────────────────────────────────────────────

  const toggleMenu = useCallback(
    (id: string, btnEl: HTMLElement) => {
      if (openMenuId === id) {
        setOpenMenuId(null);
        return;
      }
      const rect = btnEl.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
      setOpenMenuId(id);
    },
    [openMenuId],
  );

  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpenMenuId(null);
    };
    const handleScroll = () => setOpenMenuId(null);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuId]);

  // ─── Actions ──────────────────────────────────────────────────

  const handleToggleTodo = useCallback(
    async (todo: UserTodoItem) => {
      const newStatus = todo.status === 'pending' ? 'complete' : 'pending';
      const timers: ReturnType<typeof setTimeout>[] = [];
      let todoEl: HTMLElement | null = null;

      if (newStatus === 'complete') {
        playTodoSound();
        setCompletingItems((prev) => new Set(prev).add(todo.id));

        todoEl = document.querySelector(
          `[data-reminder-id="${todo.id}"]`,
        ) as HTMLElement | null;
        if (todoEl) {
          const el = todoEl;
          timers.push(setTimeout(() => el.classList.add('collapsed'), 700));
        }

        timers.push(
          setTimeout(() => {
            setCompletingItems((prev) => {
              const next = new Set(prev);
              next.delete(todo.id);
              return next;
            });
            setUserTodos((prev) =>
              prev.map((t) =>
                t.id === todo.id ? { ...t, status: newStatus } : t,
              ),
            );
          }, 1100),
        );
      } else {
        setUserTodos((prev) =>
          prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t)),
        );
      }

      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/user-todos/${todo.id}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error updating reminder:', err);
        timers.forEach(clearTimeout);
        todoEl?.classList.remove('collapsed');
        setTaskNotice(apiErrorMessage(err, 'Could not update this task.'));
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setUserTodos((prev) =>
          prev.map((t) =>
            t.id === todo.id ? { ...t, status: todo.status } : t,
          ),
        );
      }
    },
    [getAccessTokenSilently, playTodoSound],
  );

  const handleDismissTodo = useCallback(
    async (todo: UserTodoItem) => {
      setOpenMenuId(null);
      setCompletingItems((prev) => new Set(prev).add(todo.id));

      const todoEl = document.querySelector(
        `[data-reminder-id="${todo.id}"]`,
      ) as HTMLElement | null;
      if (todoEl) {
        setTimeout(() => todoEl.classList.add('collapsed'), 700);
      }

      const removeRow = setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setUserTodos((prev) => prev.filter((t) => t.id !== todo.id));
      }, 1100);

      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/user-todos/${todo.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error('Error dismissing reminder:', err);
        clearTimeout(removeRow);
        todoEl?.classList.remove('collapsed');
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(todo.id);
          return next;
        });
        setTaskNotice(apiErrorMessage(err, 'Could not remove this task.'));
      }
    },
    [getAccessTokenSilently],
  );

  const handleSetPriority = useCallback(
    async (todo: UserTodoItem, priority: string | null) => {
      setOpenMenuId(null);
      const typedPriority = priority
        ? (priority as 'high' | 'medium' | 'low')
        : undefined;

      setUserTodos((prev) =>
        prev.map((t) =>
          t.id === todo.id ? { ...t, priority: typedPriority } : t,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/user-todos/${todo.id}`,
          { priority },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error setting priority:', err);
        setTaskNotice(apiErrorMessage(err, 'Could not change the priority.'));
        setUserTodos((prev) =>
          prev.map((t) =>
            t.id === todo.id ? { ...t, priority: todo.priority } : t,
          ),
        );
      }
    },
    [getAccessTokenSilently],
  );

  const handleAssign = useCallback(
    async (todo: UserTodoItem, assigneeUserId: string | null) => {
      const members = selectedList?.members ?? [];
      const assignee = assigneeUserId
        ? members.find((m) => m.userId === assigneeUserId)
        : undefined;
      setUserTodos((prev) =>
        prev.map((t) =>
          t.id === todo.id
            ? {
                ...t,
                assigneeUserId: assigneeUserId ?? undefined,
                assigneeName: assignee?.name,
                assigneeStatus: assignee?.status,
              }
            : t,
        ),
      );
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/user-todos/${todo.id}`,
          { assigneeUserId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error assigning reminder:', err);
        setTaskNotice(apiErrorMessage(err, 'Could not assign this task.'));
        setUserTodos((prev) =>
          prev.map((t) =>
            t.id === todo.id
              ? {
                  ...t,
                  assigneeUserId: todo.assigneeUserId,
                  assigneeName: todo.assigneeName,
                  assigneeStatus: todo.assigneeStatus,
                }
              : t,
          ),
        );
      }
    },
    [getAccessTokenSilently, selectedList],
  );

  // ─── Sorted items ─────────────────────────────────────────────

  // Report an "X In-progress • Y Completed" summary to the host (app-view header).
  useEffect(() => {
    if (!onSubtitleChange) return;
    const inProgress = userTodos.filter((t) => t.status === 'pending').length;
    const completed = userTodos.filter((t) => t.status === 'complete').length;
    onSubtitleChange(`${inProgress} In-progress • ${completed} Completed`);
  }, [userTodos, onSubtitleChange]);

  const personalPendingCount = useMemo(
    () => userTodos.filter((t) => t.status === 'pending' && !t.listId).length,
    [userTodos],
  );

  const activeReminders = useMemo(() => {
    const items = userTodos.filter(
      (t) =>
        t.status === 'pending' &&
        (selectedListId === PERSONAL_LIST_ID
          ? !t.listId
          : t.listId === selectedListId),
    );

    const PRIORITY_TIER: Record<string, number> = {
      high: 2,
      medium: 3,
      low: 4,
    };

    items.sort((a, b) => {
      const aTier = a.priority
        ? (PRIORITY_TIER[a.priority] ?? 5)
        : a.dueDate
          ? 5
          : 6;
      const bTier = b.priority
        ? (PRIORITY_TIER[b.priority] ?? 5)
        : b.dueDate
          ? 5
          : 6;
      if (aTier !== bTier) return aTier - bTier;
      if (a.dueDate && b.dueDate) {
        const diff =
          new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        if (diff !== 0) return diff;
      } else if (a.dueDate) return -1;
      else if (b.dueDate) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return items;
  }, [userTodos, selectedListId]);

  // ─── Connected task provider (Todoist / Asana) ────────────────

  const taskProviderName = todoistConnected
    ? 'Todoist'
    : asanaConnected
      ? 'Asana'
      : null;

  const handleTodoCreated = (todo: UserTodoItem) => {
    setTaskNotice(null);
    setUserTodos((prev) => [todo, ...prev]);
  };
  const handleTodoRejected = (optimisticId: string, message: string) => {
    setUserTodos((prev) => prev.filter((t) => t.id !== optimisticId));
    setTaskNotice(message);
  };
  const handleTodoConfirmed = (optimisticId: string, realId: string) => {
    setUserTodos((prev) =>
      prev.map((t) => (t.id === optimisticId ? { ...t, id: realId } : t)),
    );
    fetchTodos();
  };

  // ─── Render ───────────────────────────────────────────────────

  const pageClassName = `reminders-page${isMobile ? ' reminders-page--mobile' : ''}`;

  if (loading) {
    return (
      <div className={pageClassName}>
        <TasksSkeleton />
      </div>
    );
  }

  const newButton = (
    <button
      className='reminders-new-btn'
      onClick={() => setAddTodoModalOpen(true)}
    >
      <PlusAddIcon />
      <span>New</span>
    </button>
  );

  return (
    <div className={pageClassName}>
      <div className='reminders-section'>
        {!hideHeading && (
          <div className='reminders-header'>
            <h1 className='reminders-heading'>Reminders</h1>
          </div>
        )}

        {taskProviderName && (
          <div className='todoist-banner'>
            {taskProviderName === 'Todoist' ? (
              <TodoistLogo className='todoist-icon' />
            ) : (
              <AsanaLogo className='todoist-icon' />
            )}
            <div className='todoist-banner-mid'>
              <h3 className='todoist-banner-title'>
                Your reminders are managed on {taskProviderName}
              </h3>
              <p className='todoist-banner-desc'>
                You can ask your secretary to add, read or mark as done your
                todos on {taskProviderName}. If you ask your secretary to remind
                you about something they'll add it to {taskProviderName} and
                proactively remind you themselves.
              </p>
            </div>
          </div>
        )}

        <ProjectListsBar
          lists={lists}
          invitations={invitations}
          selectedListId={selectedListId}
          onSelect={(id) => {
            setSelectedListId(id);
            setOpenMenuId(null);
          }}
          onCreate={handleCreateList}
          onRespond={handleRespondToInvite}
          personalPendingCount={personalPendingCount}
        />

        {isMobile && !selectedList && (
          <div className='project-header project-header--inbox'>
            <div className='project-header-text'>
              <h2>Inbox</h2>
            </div>
            {newButton}
          </div>
        )}

        {selectedList && (
          <div className='project-header' data-testid='project-header'>
            <div className='project-header-text'>
              <h2>{selectedList.name}</h2>
              {!isMobile && (
                <p>
                  Owned by{' '}
                  {selectedList.ownerUserId === currentUserId
                    ? 'you'
                    : selectedList.ownerName}
                  {selectedList.description
                    ? ` · ${selectedList.description}`
                    : ''}
                </p>
              )}
            </div>
            {isMobile && newButton}
            <button
              className='project-header-members'
              onClick={() => setMembersPanelOpen(true)}
              aria-label='Manage members'
            >
              <span className='project-header-avatars'>
                {selectedList.members
                  .filter((m) => m.status === 'accepted')
                  .slice(0, 4)
                  .map((m) => (
                    <MemberAvatar key={m.userId} name={m.name} size={24} />
                  ))}
              </span>
              <span>
                {
                  selectedList.members.filter((m) => m.status === 'accepted')
                    .length
                }{' '}
                member
                {selectedList.members.filter((m) => m.status === 'accepted')
                  .length === 1
                  ? ''
                  : 's'}
                {selectedList.members.some((m) => m.status === 'pending')
                  ? ` · ${selectedList.members.filter((m) => m.status === 'pending').length} invited`
                  : ''}
              </span>
            </button>
          </div>
        )}

        {taskNotice && (
          <div className='reminders-task-notice' role='alert'>
            <span>{taskNotice}</span>
            <button
              type='button'
              aria-label='Dismiss'
              onClick={() => setTaskNotice(null)}
            >
              ×
            </button>
          </div>
        )}

        {!isMobile && (
          <div className='reminders-incomplete-header'>
            <h2 className='reminders-incomplete-title'>Incomplete</h2>
            <span className='reminders-incomplete-count'>
              {activeReminders.length}
            </span>
            {newButton}
          </div>
        )}

        <div className='reminders-list'>
          {activeReminders.length === 0 && (
            <div className='reminders-empty'>
              <div className='reminders-empty-icon'>
                {isMobile ? (
                  <CheckSquareIcon />
                ) : (
                  <img
                    src='/images/empty-reminders.png'
                    alt=''
                    width={72}
                    height={72}
                  />
                )}
              </div>
              <h3 className='reminders-empty-title'>
                {selectedList
                  ? `Nothing in ${selectedList.name}${isMobile ? '' : ' yet'}`
                  : "You don't have any Reminders"}
              </h3>
              <div className='reminders-empty-desc'>
                {selectedList ? (
                  <p>
                    Choose 'New' to add a task and assign it to someone on this
                    list.
                  </p>
                ) : (
                  <>
                    <p>Ask your secretary, or choose 'New'.</p>
                    <p>
                      Open Settings to import your reminders from another app.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
          {activeReminders.map((todo) => {
            const isCompleting = completingItems.has(todo.id);
            const projectName = todo.listId
              ? null
              : todo.threadDescription || null;
            const provenance = describeTodoProvenance(todo, currentUserId);

            return (
              <div
                key={todo.id}
                className={`reminder-item${isCompleting ? ' completing' : ''}`}
                data-reminder-id={todo.id}
              >
                <button
                  className='reminder-circle'
                  onClick={() => handleToggleTodo(todo)}
                  aria-label='Mark as done'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle
                      cx='12'
                      cy='12'
                      r='11.5'
                      fill={isCompleting ? '#489FD9' : '#FFFEFC'}
                      stroke={isCompleting ? '#489FD9' : '#C1C5CD'}
                    />
                    {isCompleting && (
                      <path
                        className='tick'
                        d='M7 12L10.5 15.5L17 9'
                        stroke='white'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        fill='none'
                      />
                    )}
                  </svg>
                </button>

                <div className='reminder-mid'>
                  <p className='reminder-description'>
                    {todo.priority && PRIORITY_COLORS[todo.priority] && (
                      <span
                        className='priority-dot'
                        style={{
                          background: PRIORITY_COLORS[todo.priority],
                        }}
                      />
                    )}
                    {todo.description}
                  </p>
                  <div className='reminder-meta'>
                    {todo.dueDate && (
                      <span
                        className={`reminder-due${isDueDateOverdue(todo.dueDate) ? ' overdue' : ''}`}
                      >
                        <CalendarOutlineIcon className='calendar-icon' />
                        {formatDueDate(todo.dueDate)}
                      </span>
                    )}
                    {projectName && (
                      <span className='reminder-project'>{projectName}</span>
                    )}
                    {selectedList && (
                      <span className='reminder-people'>
                        {provenance.addedBy && (
                          <span className='reminder-creator'>
                            from {provenance.addedBy.name}
                            {provenance.addedBy.guest ? ' (guest)' : ''}
                          </span>
                        )}
                        {provenance.editedBy && (
                          <span className='reminder-creator reminder-edited'>
                            edited by {provenance.editedBy}
                          </span>
                        )}
                        <AssigneePicker
                          variant='pill'
                          members={selectedList.members}
                          value={todo.assigneeUserId ?? null}
                          currentUserId={currentUserId}
                          onChange={(assigneeUserId) =>
                            handleAssign(todo, assigneeUserId)
                          }
                        />
                      </span>
                    )}
                  </div>
                </div>

                <div
                  className='reminder-right'
                  ref={openMenuId === todo.id ? menuRef : undefined}
                >
                  <button
                    className='dots-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMenu(todo.id, e.currentTarget);
                    }}
                  >
                    <VerticalDotsIcon />
                  </button>
                  {openMenuId === todo.id &&
                    createPortal(
                      <div
                        className='reminders-dropdown-menu'
                        ref={dropdownRef}
                        style={{ top: menuPos.top, right: menuPos.right }}
                      >
                        <div className='dropdown-item has-submenu'>
                          <span>Set priority</span>
                          <span className='submenu-arrow'>›</span>
                          <div className='priority-submenu'>
                            {[
                              {
                                value: 'high',
                                label: 'High',
                                color: '#EF4444',
                              },
                              {
                                value: 'medium',
                                label: 'Medium',
                                color: '#F59E0B',
                              },
                              {
                                value: 'low',
                                label: 'Low',
                                color: '#3B82F6',
                              },
                              {
                                value: null,
                                label: 'No priority',
                                color: '#838b9b',
                              },
                            ].map((opt) => (
                              <div
                                key={opt.value ?? 'none'}
                                className={`priority-option${(todo.priority || null) === opt.value ? ' active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetPriority(todo, opt.value);
                                }}
                              >
                                <span
                                  className='priority-dot'
                                  style={{ background: opt.color }}
                                />
                                {opt.label}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div
                          className='dropdown-item dismiss'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismissTodo(todo);
                          }}
                        >
                          Dismiss
                        </div>
                      </div>,
                      document.body,
                    )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {membersPanelOpen && selectedList && (
        <ListMembersPanel
          list={selectedList}
          currentUserId={currentUserId}
          onClose={() => setMembersPanelOpen(false)}
          onRemove={(userId) => removeMember(selectedList.id, userId)}
          onRename={(name) => renameList(selectedList.id, name)}
          onArchive={() => archiveList(selectedList.id)}
        />
      )}

      {isMobile ? (
        <MobileNewTaskSheet
          open={addTodoModalOpen}
          onClose={() => setAddTodoModalOpen(false)}
          agentId={activeAgent?.id}
          listId={selectedList?.id}
          listName={selectedList?.name}
          listMembers={selectedList?.members}
          currentUserId={currentUserId}
          onTodoCreated={handleTodoCreated}
          onTodoRejected={handleTodoRejected}
          onTodoConfirmed={handleTodoConfirmed}
        />
      ) : (
        <AddTodoModal
          isOpen={addTodoModalOpen}
          onClose={() => setAddTodoModalOpen(false)}
          userFirstName={userFirstName}
          agentNickname={activeAgent?.nickname}
          agentImageUrl={activeAgent?.image_url}
          isUserTask={true}
          agentId={activeAgent?.id}
          isMobile={false}
          fixedRecipient
          predominantColour={activeAgent?.predominant_colour}
          listId={selectedList?.id}
          listName={selectedList?.name}
          listMembers={selectedList?.members}
          currentUserId={currentUserId}
          onTodoCreated={handleTodoCreated}
          onTodoRejected={handleTodoRejected}
          onTodoConfirmed={handleTodoConfirmed}
        />
      )}
    </div>
  );
};

export default RemindersPage;
